import { Controller } from 'stimulus'

export default class extends Controller {
  initialize() {
    this.mobileNavLinks = this.element.querySelectorAll('.js-mobile-nav-link')
    this.toggleButton = this.element.querySelector('.js-mobile-toggle')
    this.closeButton = this.element.querySelector('.js-mobile-close')
    this.mobileNav = this.element.querySelector('.js-mobile-nav')
    this.isOpen = false
    this.closeTimer = null
  }

  connect() {
    if (!this.toggleButton || !this.mobileNav) return

    this.handleKeydown = this.onKeydown.bind(this)

    this.toggleButton.addEventListener('click', this.handleToggle)
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.handleClose)
    }

    this.handleNavLinkClick = this.onMobileNavLinkClick.bind(this)
    this.mobileNavLinks.forEach((link) => {
      link.addEventListener('click', this.handleNavLinkClick)
    })

    this.mobileNav.setAttribute('aria-hidden', 'true')
  }

  disconnect() {
    if (this.toggleButton) {
      this.toggleButton.removeEventListener('click', this.handleToggle)
    }
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.handleClose)
    }

    this.mobileNavLinks.forEach((link) => {
      link.removeEventListener('click', this.handleNavLinkClick)
    })

    document.removeEventListener('keydown', this.handleKeydown)

    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer)
      this.closeTimer = null
    }
  }

  handleToggle = () => {
    this.isOpen ? this.closeMobileNav() : this.openMobileNav()
  }

  handleClose = () => {
    this.closeMobileNav()
  }

  openMobileNav() {
    if (!this.toggleButton || !this.mobileNav) return

    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer)
      this.closeTimer = null
    }

    this.isOpen = true
    this.element.classList.add('is-mobile-nav-open')
    document.body.classList.add('-is-scroll-locked')
    this.toggleButton.setAttribute('aria-expanded', 'true')
    this.toggleButton.setAttribute('aria-hidden', 'true')
    this.toggleButton.setAttribute('tabindex', '-1')
    this.mobileNav.setAttribute('aria-hidden', 'false')
    document.addEventListener('keydown', this.handleKeydown)

    const firstFocusable = this.getFocusableInMobileNav()[0]
    if (firstFocusable) {
      requestAnimationFrame(() => {
        firstFocusable.focus()
      })
    }
  }

  closeMobileNav = (restoreFocus = true) => {
    if (!this.toggleButton || !this.mobileNav) return

    this.isOpen = false
    this.element.classList.remove('is-mobile-nav-open')
    document.body.classList.remove('-is-scroll-locked')
    this.toggleButton.setAttribute('aria-expanded', 'false')
    this.toggleButton.removeAttribute('aria-hidden')
    this.toggleButton.removeAttribute('tabindex')
    this.mobileNav.setAttribute('aria-hidden', 'true')
    document.removeEventListener('keydown', this.handleKeydown)
    if (restoreFocus) this.toggleButton.focus()
  }

  closeMobileNavAndRun(callback, restoreFocus = false) {
    this.closeMobileNav(restoreFocus)

    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer)
    }

    this.closeTimer = window.setTimeout(() => {
      this.closeTimer = null
      callback()
    }, 300)
  }

  onMobileNavLinkClick(e) {
    if (!this.isOpen) return

    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return
    }

    const link = e.currentTarget
    const hash = this.resolveInPageHash(link)

    if (!hash) {
      this.closeMobileNav(false)
      return
    }

    e.preventDefault()
    e.stopPropagation()
    this.closeMobileNavAndRun(() => {
      history.pushState(null, '', hash)
      this.scrollToHash(hash)
    })
  }

  resolveInPageHash(link) {
    const rawHref = link.getAttribute('href') || ''
    if (!rawHref) return null

    const isBareHashLink = rawHref.startsWith('#')
    const url = new URL(link.href, window.location.origin)

    if (!url.hash) return null
    if (url.pathname !== window.location.pathname && !isBareHashLink) return null

    const target = document.getElementById(url.hash.slice(1))
    if (!target) return null

    return url.hash
  }

  scrollToHash(hash) {
    const target = document.getElementById(hash.slice(1))
    if (!target) return

    const headerHeight =
      parseInt(getComputedStyle(document.documentElement).getPropertyValue('--headerHeight'), 10) || 0
    const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerHeight + 1)
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    window.scrollTo({ top, behavior: prefersReducedMotion ? 'auto' : 'smooth' })
  }

  onKeydown(e) {
    if (!this.isOpen || !this.mobileNav) return

    if (e.key === 'Escape') {
      this.closeMobileNav()
      return
    }

    if (e.key !== 'Tab') return

    const focusable = this.getFocusableInMobileNav()

    if (!focusable.length) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (!focusable.includes(document.activeElement)) {
      e.preventDefault()
      first.focus()
      return
    }

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  getFocusableInMobileNav() {
    if (!this.mobileNav) return []

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',')

    return Array.from(this.mobileNav.querySelectorAll(focusableSelector)).filter((el) => {
      if (el.getAttribute('aria-hidden') === 'true') return false
      return el.offsetParent !== null
    })
  }
}
